import { Link } from "@/components/ui/link";

import { PositiveTractionLogo } from "@/assets/icons/positive-traction-icon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import { cn } from "@/lib/utils";
import { A, type AnchorProps, useLocation, useMatch } from "@solidjs/router";
import MenuIcon from "lucide-solid/icons/menu";
import {
  type ComponentProps,
  For,
  type ParentProps,
  Show,
  createSignal,
  splitProps,
} from "solid-js";

type NavConfig = {
  title: string;
  href: string;
  label?: string;
  badge?: string;
};

const navConfig: NavConfig[] = [
  {
    title: "Home",
    href: "/",
  },
  {
    title: "Facilitation Arena",
    href: "/arena",
    badge: "Free",
  },
  {
    title: "Instructors",
    href: "/instructors",
  },
] as const;

interface MainNavProps extends ComponentProps<"div"> {}

export function MainNav(props: MainNavProps) {
  const [local, rest] = splitProps(props, ["class"]);
  const location = useLocation();
  const isSelected = useMatch(() => location.pathname);

  return (
    <div class={cn("mr-4 hidden md:flex", local.class)} {...rest}>
      <A href="/" class="group mr-4 flex items-baseline gap-x-4 lg:mr-6">
        <span class="flex">
          <PositiveTractionLogo class="size-5" />
        </span>
        <span class="hidden text-nowrap align-baseline font-semibold opacity-0 transition-all transition-discrete sm:text-2xl sm:opacity-100 md:inline-block">
          <span class="text-cinnabar transition-colors duration-300 group-hover:text-cinnabar/90">
            Positive
          </span>{" "}
          <span class="text-azul transition-colors duration-300 group-hover:text-azul/90">
            Traction
          </span>
        </span>
      </A>
      <nav class="flex items-center gap-4 text-sm xl:gap-6">
        <For each={navConfig}>
          {(item) => (
            <Link
              href={item.href}
              bool:aria-selected={isSelected()}
              class={cn(
                "group",
                "text-balance transition-colors hover:text-foreground/80",
                "text-foreground/80 aria-selected:text-foreground",
              )}
            >
              {item.title}
              <Show when={item.badge}>
                <span>
                  <Badge
                    variant="default"
                    class={cn(
                      "no-underline! ml-2 rounded-md px-1 py-0.5 font-medium uppercase",
                      "text-green-100 hover:text-green-50",
                      "bg-green-600 hover:bg-green-700 group-hover:bg-green-700",
                      "decoration-0",
                    )}
                  >
                    {item.badge}
                  </Badge>
                </span>
              </Show>
            </Link>
          )}
        </For>
      </nav>
    </div>
  );
}

export function MobileNav() {
  const [open, setOpen] = createSignal(false);

  return (
    <Drawer open={open()} onOpenChange={setOpen}>
      <DrawerTrigger
        as={Button}
        variant="ghost"
        class="h-8 w-full justify-between gap-4 px-0 text-base hover:bg-transparent focus-visible:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 md:hidden"
      >
        <MenuIcon class="!size-6" />
        <span class="sr-only">Toggle Menu</span>
      </DrawerTrigger>
      <DrawerContent class="max-h-[80svh] p-0">
        <div class="overflow-auto p-6">
          <div class="flex flex-col space-y-2">
            <div class="flex flex-col gap-4 pt-6">
              <h4 class="font-medium text-xl">Where to?</h4>
              <For each={navConfig}>
                {(item) => (
                  <Show when={item.href} fallback={item.title}>
                    <MobileLink
                      href={item.href}
                      onOpenChange={setOpen}
                      class="opacity-80"
                    >
                      {item.title}
                      <Show when={item.label}>
                        <span class="ml-2 rounded-md bg-[#adfa1d] px-1.5 py-0.5 text-[#000000] text-xs leading-none no-underline group-hover:no-underline">
                          {item.label}
                        </span>
                      </Show>
                    </MobileLink>
                  </Show>
                )}
              </For>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

interface MobileLinkProps
  extends AnchorProps,
    ParentProps<{
      onOpenChange?: (open: boolean) => void;
    }> {}

function MobileLink(props: MobileLinkProps) {
  const [local, rest] = splitProps(props, [
    "href",
    "onOpenChange",
    "class",
    "children",
  ]);

  return (
    <A
      href={local.href}
      onClick={() => {
        local.onOpenChange?.(false);
      }}
      class={cn("text-[1.15rem]", local.class)}
      {...rest}
    >
      {local.children}
    </A>
  );
}
